// Entry point. Reads the URL, opens a transport for board routes, and
// wires the state subscriber that re-renders the matrix on change.

import { renderBoard } from "./render/matrix";
import { renderLanding } from "./render/landing";
import { applyStoredTheme, followSystemTheme, watchSystemTheme } from "./render/theme";
import { updateCursors, unmountCursorLayer } from "./render/presence";
import { showFilterBar } from "./render/search";
import { mountCursorTracker, unmountCursorTracker } from "./input/cursor";
import { mountDragDrop, unmountDragDrop } from "./input/dragdrop";
import {
  applyServerMsg,
  attachTransport,
  detachTransport,
  hydrateFromCache,
  lastSeq,
  local,
  pendingOpsForTransport,
  setConnection,
  subscribe,
} from "./state";
import { isValidBoardId, parseBoardRef } from "./shared/ids";
import { isTauri } from "./config/backend";
import { syncQueryFromHash } from "./search";
import { readBoardCache, writeBoardCache } from "./storage/board-cache";
import { listRecentBoards, recordRecentBoard } from "./storage/recent-boards";
import { createTransport, type Transport } from "./transport";

// Theme startup. In the desktop/mobile apps we defer entirely to the OS
// appearance (and hide the manual toggle — see render/matrix.ts), so drop any
// stored override and re-render on live OS light/dark switches. On the web we
// restore the user's explicit choice before the first render; with no stored
// choice this is a no-op and `prefers-color-scheme` drives the palette.
if (isTauri()) {
  followSystemTheme();
  watchSystemTheme(() => route());
} else {
  applyStoredTheme();
}

// Wire up PWA metadata (manifest + icons) at runtime. These reference files
// shipped at the dist root (client/public/ → served by Workers Assets); they're
// injected here rather than in index.html so the Bun HTML bundler doesn't try
// to resolve the absolute public paths at build time.
function installPwaLinks(): void {
  const links: Array<[string, Record<string, string>]> = [
    ["link", { rel: "manifest", href: "/manifest.webmanifest" }],
    ["link", { rel: "icon", href: "/favicon.ico", sizes: "any" }],
    ["link", { rel: "apple-touch-icon", href: "/icons/icon-192.png" }],
  ];
  for (const [tag, attrs] of links) {
    const el = document.createElement(tag);
    for (const [k, v] of Object.entries(attrs)) el.setAttribute(k, v);
    document.head.appendChild(el);
  }
}
installPwaLinks();

const app = document.getElementById("app");
if (!app) throw new Error("missing #app");

let transport: Transport | null = null;
let currentBoardId: string | null = null;

function route(): void {
  const path = window.location.pathname;
  if (path === "/" || path === "") {
    teardownBoard();
    renderLanding(app!);
    return;
  }
  const boardId = path.slice(1);
  if (!isValidBoardId(boardId)) {
    teardownBoard();
    app!.innerHTML = "";
    const h = document.createElement("h1");
    h.textContent = "Not found";
    app!.append(h);
    return;
  }
  mountBoard(boardId);
}

function mountBoard(boardId: string): void {
  if (currentBoardId === boardId) {
    renderBoard(app!, boardId);
    afterRender();
    return;
  }
  teardownBoard();
  currentBoardId = boardId;
  // Seed state from the per-board localStorage cache (if any) before the
  // transport opens. The first render then paints against real tasks, and
  // the cached `seq` lets the server reply with a delta `replay` instead
  // of a full `snapshot` when the cache is still in-window.
  const cached = readBoardCache(boardId);
  if (cached) hydrateFromCache(cached.state, cached.seq);
  // Add/refresh this board in the recent-boards list for the desktop
  // switcher. The title is refined once the server snapshot lands (see the
  // throttled cache-write below); "Eyes" is the placeholder fallback.
  recordRecentBoard(boardId, cached?.state.title ?? "Eyes");
  transport = createTransport(boardId, {
    onConnected: () => setConnection("connected"),
    onReconnecting: () => setConnection("reconnecting"),
    onOffline: () => setConnection("offline"),
    onServerMessage: (msg) => applyServerMsg(msg),
    lastSeq,
    pendingOps: pendingOpsForTransport,
  });
  attachTransport(transport);
  renderBoard(app!, boardId);
  afterRender();
}

function teardownBoard(): void {
  if (transport) {
    transport.close();
    detachTransport();
    transport = null;
  }
  if (cacheWriteTimer !== undefined) {
    window.clearTimeout(cacheWriteTimer);
    cacheWriteTimer = undefined;
  }
  unmountCursorTracker();
  unmountCursorLayer();
  unmountDragDrop();
  currentBoardId = null;
}

// Coalesce localStorage writes across rapid op bursts (typing, drag).
let cacheWriteTimer: number | undefined;
const CACHE_WRITE_DELAY_MS = 300;

function scheduleCacheWrite(): void {
  if (cacheWriteTimer !== undefined) return;
  cacheWriteTimer = window.setTimeout(() => {
    cacheWriteTimer = undefined;
    if (currentBoardId !== null && local.seq > 0) {
      writeBoardCache(currentBoardId, local.state, local.seq);
      // Keep the recent-boards title in sync with the live board title.
      recordRecentBoard(currentBoardId, local.state.title ?? "Eyes");
    }
  }, CACHE_WRITE_DELAY_MS);
}

function afterRender(): void {
  const matrix = document.getElementById("matrix");
  if (matrix) {
    mountCursorTracker(matrix);
    mountDragDrop(matrix);
  }
}

subscribe((kind) => {
  if (currentBoardId === null) return;
  if (kind === "cursor-only") {
    updateCursors();
    return;
  }
  renderBoard(app!, currentBoardId);
  afterRender();
  // Persist after every state-affecting change. Throttled so a burst of
  // edits doesn't hammer JSON.stringify + setItem; gated on seq > 0 so we
  // don't overwrite the cache with the empty pre-snapshot state.
  scheduleCacheWrite();
});

// Read `#q=…` (if any) into local.query before the first render, and
// mount a hashchange listener so back/forward + manual URL edits stay
// in sync with the filter bar.
syncQueryFromHash();

// Global shortcut: `/` opens the filter bar (when not typing into a
// real input or contenteditable element). Mirrors GitHub / GitLab.
window.addEventListener("keydown", (e) => {
  if (e.key !== "/" || e.ctrlKey || e.metaKey || e.altKey) return;
  const t = e.target as HTMLElement | null;
  if (!t) return;
  if (
    t.matches("input, textarea, select") ||
    t.isContentEditable
  ) {
    return;
  }
  e.preventDefault();
  showFilterBar();
});

window.addEventListener("popstate", route);

// Deep-link entry point for the Tauri apps. The Rust deep-link handler
// (app/src-tauri) evaluates `window.__eyesOpenDeepLink(url)` when the OS hands
// the app an `https://eyes.wagmorelabs.com/<boardId>` App Link (Android) or an
// `eyes://board/<boardId>` custom-scheme URL (desktop). We parse out the board
// id and route to it in-app without a full reload. No-op / harmless on the web.
function openDeepLink(rawUrl: string): void {
  const boardId = parseBoardRef(rawUrl);
  if (boardId) navigateToBoard(boardId);
}

/** Navigate the SPA to `boardId` in place (no full reload), the same primitive
 *  the landing "create" button uses. Shared with the deep-link handler and the
 *  desktop board switcher (render/boards.ts). */
export function navigateToBoard(boardId: string): void {
  if (!isValidBoardId(boardId)) return;
  window.history.pushState({}, "", "/" + boardId);
  route();
}
(window as unknown as { __eyesOpenDeepLink: (u: string) => void })
  .__eyesOpenDeepLink = openDeepLink;

// Register the service worker for offline app-load + installability on the web.
// Skipped inside Tauri (assets are already local; some platforms even use an
// http tauri.localhost origin) and when the SW API is absent.
if (
  "serviceWorker" in navigator &&
  !isTauri() &&
  window.location.protocol.startsWith("http")
) {
  window.addEventListener("load", () => {
    navigator.serviceWorker.register("/sw.js").catch(() => {
      // Offline app-load is a progressive enhancement; ignore failures.
    });
  });
}

// Desktop apps have no URL bar, so a returning user should reopen their last
// board rather than the welcome screen. On a cold start at "/", jump to the
// most-recent board if there is one; first-time users still see the landing.
if (isTauri() && window.location.pathname === "/" && listRecentBoards().length > 0) {
  navigateToBoard(listRecentBoards()[0]!.id);
} else {
  route();
}
// If we landed with a `#q=…` filter pre-set, reveal the bar so the
// user sees the active query (and can edit or clear it). Same on
// external hashchange (browser back/forward, manual URL edits).
if (local.query) showFilterBar();
window.addEventListener("hashchange", () => {
  if (local.query) showFilterBar();
});
